export interface GetRewardResponse {
	amountEarned: number;
	messageEngine: string;
	engineResponse: {
		type: string;
		params: {
			points: number;
			currentLadderStep: number;
			[key: string]: unknown;
		};
	};
}

export interface RequestEnginePayload {
	requestEngine: {
		nameRule: string;
		fact: {
			dynamicFact: {
				factNameDynamic: string;
				token: string;
				urlDynamicFact: string;
			};
			factNameInputs: string;
			calculateFact: {
				factNameCalculate: string;
			};
		};
		data: {
			accountId: string;
			eventType: string;
			event: string;
			currentLadderStep: number;
			organizationId: string;
			[key: string]: unknown;
		};
	};
}

export enum EventTypeEnum {
	JOURNAL = "JOURNAL",
	STEP_COMPLETION = "STEP_COMPLETION",
	GOAL_COMPLETION = "GOAL_COMPLETION",
	SUPERVISION_ACTIVITY = "SUPERVISION_ACTIVITY",
	IN_HOUSE_SERVICE = "IN_HOUSE_SERVICE",
	EXTERNAL_PROVIDER_SERVICE = "EXTERNAL_PROVIDER_SERVICE",
	REDEEMED = "REDEEMED",
}

export enum RewardConfigurationType {
	FixedLadder = "FIXED_LADDER",
	ProgrammableLadder = "PROGRAMMABLE_LADDER",
}

export interface FixedLadderConfiguration {
	index: number;
	points: number;
}

export interface ProgrammableLadderConfiguration {
	startValue: number;
	stepValue: number;
	maxValue: number;
	resetValue: number;
}

export interface InvalidResult {
	invalidResultType?: string;
	rewardForInvalid?: string;
	resetRuleType?: string;
	specificIndex?: number;
}

export interface EventResultCondition {
	relativePosition: number; // -1 for last event, -2 for second to last, etc.
	checkType: string; // "IS_VALID", "IS_INVALID", "EQUALS_RESULT_ID", "NOT_EQUALS_RESULT_ID"
	resultId?: string; // Specific result ID to check against (for EQUALS/NOT_EQUALS)
	description?: string; // Human-readable description of the rule
	targetStep?: number; // Step to reset to (for certain reset types)
	action?: string; // "RESET_TO_STEP", "PUSH_STEP"
}

export interface RecoveryConfiguration {
	enabled: boolean;
	type?: string; // "PUSH_STEP", "REPLACE_STEP", or "LAST_STEP"
	resetToStep?: number; // Which step to reset to (default: 1)
	recoverInSteps?: number; // How many visits to catch back up to normal (for PUSH_STEP and LAST_STEP)
	description?: string;
}

export interface CustomLadderReset {
	enabled: boolean;
	conditions: EventResultCondition[]; // Conditions that trigger a reset
	validResultIds: string[]; // Result IDs considered "valid" (e.g., ["NEG", "NEGATIVE"])
	invalidResultIds: string[]; // Result IDs considered "invalid" (e.g., ["POS", "POSITIVE"])
	recovery?: RecoveryConfiguration; // Recovery configuration after reset (Push Step vs Replace Step)
	maxHistoryCheck?: number; // How many previous events to check (default: all)
}

export interface NeutralResetRule {
	resultId: string; // Result ID to track (e.g., "Dilute", "Missing")
	consecutiveCount: number; // Number of consecutive occurrences to trigger reset
}

export interface CustomNeutralReset {
	enabled: boolean;
	rules: NeutralResetRule[]; // Rules for detecting repeating neutral patterns
	description?: string;
}

export interface RewardsEngine {
	_id?: string; // Optional for cloning
	accountId: string;
	programId?: string;
	eventType: string;
	event: string;
	rewardConfigurationType: RewardConfigurationType;
	rewardBasedOnResult?: boolean;
	validValue?: string;
	invalidValue?: string;
	orgUnitIds?: string[];
	fixedLadderConfiguration?: FixedLadderConfiguration[];
	programmableLadderConfiguration?: ProgrammableLadderConfiguration[];
	invalidResult?: InvalidResult;
	customLadderReset?: CustomLadderReset;
	customNeutralReset?: CustomNeutralReset;
	maxRewardAmount?: number;
	maxOccurrences?: number;
	createdAt?: string; // Optional for cloning
	updatedAt?: string; // Optional MongoDB timestamp
	createdBy: string;
	isDeleted?: boolean; // Optional
	__v?: number; // Optional MongoDB version key
}
